// LA FILA DE PASTILLAS DE LA CAJA: qué está por hacer el usuario y con qué defaults —
// ADR-2026-09-21-las-preferencias-salen-a-la-caja-como-pastillas,
// ADR-2026-09-21-el-switch-de-imagen-o-video y
// ADR-2026-09-21-todo-lo-del-menu-que-no-adjunta-sale-a-la-fila-de-pastillas.
//
// DOS CONTROLES Y NADA MÁS, iguales en la computadora y en el teléfono
// (ADR-2026-09-24-la-fila-queda-en-inteligencia-y-un-engranaje):
//
//   • La **Inteligencia** —cuánto piensa el agente este turno— como pastilla a la vista.
//     Es lo único de la fila que se cambia seguido y lo único que vale para el turno.
//   • Un **engranaje** que abre TODAS las preferencias de generación: arriba el
//     **Formato**, uno solo para las dos familias
//     (ADR-2026-09-24-el-formato-es-uno-solo-y-vive-afuera-de-las-familias), y debajo una
//     sección Imagen (Calidad) y una sección Video (Calidad, Resolución). Las dos
//     SIEMPRE, sin que haya que elegir antes una modalidad.
//
// El switch Agente / Imagen / Video se fue de la fila. `tipoDeSalida` NO se murió —sigue
// en las preferencias, viaja al prompt y el agente la puede cambiar con su herramienta
// (`herramientaPreferencias`)—: lo que se fue es la obligación de que el usuario la
// eligiera a mano antes de escribir. Queda en `agente`, que es lo que ya era el default:
// el agente deduce del pedido si es imagen o video, y lo que el mensaje pida gana igual.
// Las preferencias de las dos familias están puestas de antemano, así que la que termine
// usando ya sabe con qué formato y con qué calidad.
//
// El **Motor** de imagen y las **Herramientas** del chat estuvieron acá y se fueron a
// Configuración → Funciones avanzadas
// (ADR-2026-09-24-el-motor-y-las-herramientas-viven-en-configuracion): son de las que se
// ponen una vez. Su estado no se movió —sigue en `ChatAtlasContext`—, sólo el lugar
// donde se tocan.
//
// El Tipo de video es la MISMA pill del Creador de video (`TipoDeVideoPill`, con su
// catálogo de tipos en cards) en la forma de esta fila: dice "General" —que no es un tipo
// sino su ausencia— hasta que se elige uno
// (ADR-2026-09-22-el-tipo-de-video-tambien-en-la-caja-del-agente). No es un `CampoDePastilla`
// como las otras: su menú no es una grilla de chips ni una lista de filas sino cards 9:16
// con el clip de ejemplo, así que se dibuja al lado y trae su propio panel.
//
// Todas vivían adentro del menú del "+" (pantallas suyas y, la Inteligencia, un botón
// aparte en la fila de abajo). El menú quedó siendo lo que dice ser: de dónde sale lo que
// se adjunta. Lo que CONFIGURA cómo se genera la pieza está a la vista, en una pastilla
// que además dice en qué está.
//
// Es el MISMO estado que tenían adentro del menú (`PreferenciasDeGeneracion` en
// `localStorage`, el nivel de inteligencia): esto es un atajo a lo que ya existía, no una
// segunda copia. Por eso la caja que muestra las pastillas ESCONDE esas entradas del menú
// y no dibuja el selector de Inteligencia de la fila de abajo — la misma cosa dos veces en
// la misma caja es la forma de que se desincronicen en la cabeza del usuario.
//
// ── UN SOLO PANEL, COLGADO DE LA PASTILLA QUE SE TOCÓ ─────────────────────
// El popover es UNO solo —un componente, un estado— pero se planta pegado a SU pastilla:
// arranca en el borde inferior IZQUIERDO de la que se tocó y mide la MITAD del ancho de la
// caja (ADR-2026-09-22-el-panel-cuelga-de-su-pastilla). Del ancho de la caja entero se veía
// como otro bloque de la pantalla y obligaba a buscar de qué pastilla había salido; a la
// mitad y bajo su pastilla, el panel ES la pastilla abierta.
//
// Lo que aquella versión cuidaba —que el de "Formato", ocho opciones, no se saliera de la
// pantalla cuando la pastilla está a la derecha— se cuida midiendo: si el panel se pasara
// del borde derecho, se corre a la izquierda lo justo para entrar (`MARGEN_DE_PANTALLA`).
// Y el ancho tiene un piso (`ANCHO_MINIMO_PANEL`): en el teléfono la mitad de la caja son
// ~170px y "1080p · 1 crédito" no entra en un chip de ese ancho.
//
// ARRIBA de la caja estuvo unas horas, y se fue por pedido del usuario: el menú tiene que
// abrirse donde el ojo ya está, que es la fila que acaba de tocar, y no en el otro extremo
// de la caja. Lo que aquel lugar cuidaba —no tapar lo que se está escribiendo— se cuida
// igual acá, porque la fila vive DEBAJO del texto: el panel se despliega sobre la fila de
// acciones y sobre lo que haya abajo de la caja, nunca sobre el pedido a medio escribir.
//
// Va por portal y `fixed`, como el menú del "+" y por el mismo motivo: en el arranque del
// chat la caja vive adentro del scroller de la conversación, y un `absolute` que se pase
// del borde queda recortado.
//
// ── TRES ANATOMÍAS DE PANEL ───────────────────────────────────────────────
// El panel es uno solo, pero lo que muestra adentro depende de qué se está eligiendo:
//
//  • `pastillas` — Formato y Calidad: valores cortos que se explican con su nombre, así
//    que entran ocho en una grilla de chips.
//  • `filas` — Inteligencia: cada opción necesita una línea que diga para qué es, y con
//    subtítulo no entran como chips. Es la misma anatomía que tenía adentro del menú
//    del "+". La cuarta, `interruptores`, se fue con las Herramientas.
//  • `segundos` — Duración: un slider de 1 s al tope de la calidad
//    (ADR-2026-09-21-la-duracion-se-elige-al-segundo), el mismo control que la card de
//    video.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Brain,
  Check,
  ChevronDown,
  Film,
  Gauge,
  Image as ImageIcon,
  MonitorPlay,
  Lock,
  Ratio,
  Settings,
} from 'lucide-react';
import { ATRIBUTO_CAJA } from './vueloAlComposer';
import { useEsMobile } from '../../hooks/useEsMobile';
import { propsDeRadio } from './radiosConTeclado';
// Los tres niveles de inteligencia son los del asistente, con sus bajadas.
import { INTEL_OPTIONS } from '../../pages/v2/modes/asistente/AyudaIAMenu';
import {
  INTELIGENCIA_BASE,
  INTELLIGENCE_LABEL,
  type Intelligence,
} from '../../pages/v2/agentModels';
import {
  CALIDADES_IMAGEN,
  CALIDADES_VIDEO,
  FORMATOS_IMAGEN,
  FORMATOS_VIDEO,
  RESOLUCIONES_VIDEO,
  type PreferenciasDeGeneracion,
} from '../preferenciasDeGeneracion';
import { AYUDA_BORRADOR } from '../../lib/resolucionVideo';
import { ladoDelPanel } from './ladoDelPanel';

/** Lo que muestra la pastilla cuando el campo está en "Automático". */
const AUTO = 'Auto';

/** La "clave" del engranaje en el estado `abierto`. No es un campo: es el menú entero. */
const CLAVE_CONFIG = 'configuracion';

/** Lo que mide el panel del engranaje: más ancho que el de una pastilla sola, porque
 *  adentro van las dos familias con sus chips. Igual entra en el teléfono, donde el
 *  tope de pantalla manda. */
const ANCHO_PANEL_CONFIG = 380;

/** Aire entre la fila de pastillas y el panel. El mismo que usa el menú del "+". */
const SEPARACION_DE_LA_FILA = 8;

/** Lo que el panel deja libre contra los bordes de la pantalla cuando hay que correrlo. */
const MARGEN_DE_PANTALLA = 8;

/** El piso del ancho: la mitad de la caja, pero nunca menos que esto (ni más que la
 *  pantalla). Con menos, las opciones con bajada —"2K · 2 créditos"— envuelven en dos
 *  renglones y la grilla de chips deja de leerse de un vistazo. */
const ANCHO_MINIMO_PANEL = 260;

/** El nombre del formato entra entero en la pantalla del menú, pero en una pastilla de la
 *  caja "Historias/Reels (9:16)" se come el renglón: se muestra la proporción, que es lo
 *  que el usuario está eligiendo de verdad. El nombre completo sigue en el panel. */
const proporcionDe = (nombre: string): string => nombre.match(/\(([^)]+)\)/)?.[1] ?? nombre;

/**
 * El CUADRITO de un formato: un rectángulo dibujado con la proporción que dice la opción
 * ("Historias/Reels (9:16)" → un rectángulo 9:16) —
 * ADR-2026-09-24-los-formatos-se-ven-con-su-proporcion.
 *
 * Es el chip más visual que se puede hacer sin un ícono por formato: la proporción no se
 * ilustra, ES la forma. Entra en una caja de 16×13, así que un 21:9 y un 9:16 se
 * distinguen de un vistazo sin que ninguno empuje el renglón.
 *
 * Sin proporción en el nombre —"Automático"— no dibuja nada.
 */
const GlifoDeProporcion: React.FC<{ etiqueta: string; activo: boolean }> = ({
  etiqueta,
  activo,
}) => {
  const m = etiqueta.match(/(\d+)\s*:\s*(\d+)/);
  if (!m) return null;
  const w = Number(m[1]);
  const h = Number(m[2]);
  if (!w || !h) return null;
  const CAJA_ANCHO = 16;
  const CAJA_ALTO = 13;
  const escala = Math.min(CAJA_ANCHO / w, CAJA_ALTO / h);
  return (
    <span
      aria-hidden="true"
      className={`inline-block shrink-0 rounded-[2px] border-[1.5px] ${
        activo ? 'border-brand-primary' : 'border-[#02062B]/[0.35]'
      }`}
      style={{ width: Math.max(4, w * escala), height: Math.max(4, h * escala) }}
    />
  );
};

/** Una opción de un campo de elección única. */
interface OpcionDeCampo {
  id: string | number | null;
  etiqueta: string;
  detalle?: string;
  /** El plan no la habilita: se dibuja igual, con candado y sin poder elegirse (hoy, los
   *  dos niveles de inteligencia arriba de "Baja" —
   *  ADR-2026-09-12-la-inteligencia-del-chat-es-de-pro-para-arriba). */
  bloqueado?: boolean;
}

/** Qué muestra el panel adentro. Ver la nota de arriba. */
type AnatomiaDelPanel = 'pastillas' | 'filas';

/** Un campo de la fila: qué dice la pastilla y qué abre el panel. */
interface CampoDePastilla {
  clave: string;
  rotulo: string;
  ayuda: string;
  /** El ícono que abre la pastilla. TODAS lo llevan: en una fila de seis pastillas que
   *  empiezan con una palabra gris, el ícono es lo que deja encontrar la que se busca sin
   *  leerlas todas. Lo estrenó la Inteligencia (su cerebrito venía de cuando era un botón
   *  aparte) y ahora es la anatomía de la fila entera. */
  Icono: typeof Brain;
  /** Lo que se lee al lado del rótulo (el valor elegido, o "Auto"). */
  valor: string;
  /** El valor NO es el de por defecto: la pastilla lo pinta en azul. */
  puesto: boolean;
  anatomia: AnatomiaDelPanel;
  // ── elección única (`pastillas` y `filas`) ──
  opciones?: OpcionDeCampo[];
  elegido?: string | number | null;
  elegir?: (v: string | number | null) => void;
  /** Antepone "Automático" (= sin preferencia, `null`) a las opciones. Los campos cuya
   *  lista YA trae su opción por defecto —Atlas en Motor, Baja en Inteligencia— no la
   *  quieren: sería una segunda forma de decir lo mismo. */
  conAutomatico?: boolean;
}

export interface PastillasDeContenidoProps {
  preferencias: PreferenciasDeGeneracion;
  onCambiar: <K extends keyof PreferenciasDeGeneracion>(
    clave: K,
    valor: PreferenciasDeGeneracion[K],
  ) => void;
  /** El nivel de inteligencia, su setter y si el plan deja subirlo. */
  inteligencia: Intelligence;
  onCambiarInteligencia: (nivel: Intelligence) => void;
  puedeSubirInteligencia: boolean;
  /** Cómo se planta la fila en la caja. Lo pone el llamador porque la fila COMPARTE
   *  renglón con el mic y el enviar (ADR-2026-09-22-el-mic-y-el-enviar-van-a-la-derecha-de-las-pastillas):
   *  cuánto mide y qué padding tiene es del renglón, no de las pastillas. */
  className?: string;
  /**
   * Sin la palabra de cada pastilla también en desktop ("Baja" en vez de "Inteligencia
   * Baja"), la forma que ya tienen en el teléfono. La pide la barra cuando la fila es
   * angosta y el "+" lleva "Agregar"
   * (ADR-2026-09-25-agregar-con-rotulo-en-el-panel-y-con-el-lienzo). Por defecto, como
   * siempre.
   */
  compactas?: boolean;
}

export const PastillasDeContenido: React.FC<PastillasDeContenidoProps> = ({
  preferencias,
  onCambiar,
  inteligencia,
  onCambiarInteligencia,
  puedeSubirInteligencia,
  className = '',
  compactas = false,
}) => {
  // Qué campo tiene el panel abierto (su `clave`), o null. Uno a la vez: es un panel solo.
  const [abierto, setAbierto] = useState<string | null>(null);
  /** En el TELÉFONO la fila es otra cosa — ver la nota "LA FILA EN EL TELÉFONO". */
  const esMobile = useEsMobile();
  /** La hoja de Configuración (sólo teléfono): todas las opciones, una debajo de la otra. */
  const [hojaAbierta, setHojaAbierta] = useState(false);
  const filaRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  /** El botón de cada pastilla, por `clave`: de ahí sale dónde se planta el panel. Es un
   *  mapa y no un ref por campo porque la fila se arma con un `map` y los campos cambian
   *  con la mitad elegida (Imagen / Video). */
  const pastillasRef = useRef(new Map<string, HTMLButtonElement>());
  // Dónde se planta el panel: el ancho y el borde de arriba de la CAJA (no de la fila).
  const [pos, setPos] = useState<{
    left: number;
    width: number;
    /** Abriendo abajo: el borde de abajo de la pastilla, de ahí cuelga el panel. */
    top?: number;
    /** Abriendo arriba: la distancia del piso al borde de arriba de la pastilla, así el
     *  panel se apoya sobre ella (ADR-2026-09-25-el-panel-de-las-pastillas-abre-hacia-
     *  donde-hay-lugar). */
    bottom?: number;
    /** Lo que queda entre la pastilla y el borde de la pantalla de ese lado: no puede
     *  medir más que eso (scrollea adentro). */
    alto: number;
  } | null>(null);
  /** La última `medir`, para volver a medir cuando el panel ya montó y se sabe su alto. */
  const medirRef = useRef<() => void>(() => {});

  // Se mide al abrir y se vuelve a medir mientras esté abierto (la ventana cambia de
  // tamaño, la página scrollea): con `fixed`, si no se resigue, el panel se queda flotando
  // donde estaba la caja.
  useLayoutEffect(() => {
    if (!abierto) {
      setPos(null);
      return;
    }
    const medir = () => {
      const fila = filaRef.current;
      const caja = (fila?.closest(`[${ATRIBUTO_CAJA}]`) as HTMLElement | null) ?? fila;
      const pastilla = pastillasRef.current.get(abierto);
      if (!fila || !caja) return;
      const c = caja.getBoundingClientRect();
      // De dónde cuelga: del borde de abajo de SU pastilla, alineado a su borde izquierdo.
      // Sin la pastilla medida —no debería pasar— cae a la fila entera, que es lo que
      // hacía antes.
      const ancla = (pastilla ?? fila).getBoundingClientRect();
      // La MITAD de la caja, con el piso de `ANCHO_MINIMO_PANEL` y sin pasarse de la
      // pantalla (en el teléfono la caja ya es casi toda la pantalla). El del ENGRANAJE
      // pide más: adentro van las dos familias, y a media caja los chips de formato
      // envuelven de a uno por renglón.
      const deseado =
        abierto === CLAVE_CONFIG
          ? Math.max(c.width / 2, ANCHO_PANEL_CONFIG)
          : Math.max(c.width / 2, ANCHO_MINIMO_PANEL);
      const width = Math.min(deseado, window.innerWidth - MARGEN_DE_PANTALLA * 2);
      // Arranca en el borde izquierdo de la pastilla; si con ese ancho se saliera por la
      // derecha, se corre lo justo para entrar (y nunca antes del margen izquierdo).
      const left = Math.max(
        MARGEN_DE_PANTALLA,
        Math.min(ancla.left, window.innerWidth - width - MARGEN_DE_PANTALLA),
      );
      // De qué lado: abajo si entra (la caja de la home, en el medio de la pantalla),
      // arriba si entra (la caja del Agente, al pie), y si no del lado con más aire.
      // Antes colgaba siempre abajo y en el Agente nacía contra el borde de la pantalla
      // (ADR-2026-09-25-el-panel-de-las-pastillas-abre-hacia-donde-hay-lugar). El alto del
      // contenido se sabe recién con el panel montado: la primera vez decide por el aire
      // y se vuelve a medir antes de pintar (ver el efecto de abajo).
      const aire = SEPARACION_DE_LA_FILA * 2;
      const espacioAbajo = window.innerHeight - ancla.bottom - aire;
      const espacioArriba = ancla.top - aire;
      const lado = ladoDelPanel({
        espacioAbajo,
        espacioArriba,
        altoContenido: panelRef.current?.scrollHeight ?? null,
      });
      setPos(
        lado === 'abajo'
          ? { left, width, top: ancla.bottom + SEPARACION_DE_LA_FILA, alto: Math.max(160, espacioAbajo) }
          : {
              left,
              width,
              bottom: window.innerHeight - ancla.top + SEPARACION_DE_LA_FILA,
              alto: Math.max(160, espacioArriba),
            },
      );
    };
    medirRef.current = medir;
    medir();
    window.addEventListener('resize', medir);
    // `true`: el scroll de la conversación no burbujea hasta `window`.
    window.addEventListener('scroll', medir, true);
    return () => {
      window.removeEventListener('resize', medir);
      window.removeEventListener('scroll', medir, true);
    };
  }, [abierto]);
  // Con el panel recién montado ya se sabe su alto: se vuelve a medir para elegir el lado
  // con el contenido real. Es un layout effect, así que pasa antes del primer pintado.
  const panelMontado = pos !== null;
  useLayoutEffect(() => {
    if (panelMontado) medirRef.current();
  }, [panelMontado, abierto]);

  // Cerrar al tocar afuera y con Escape. "Afuera" es fuera de la fila Y fuera del panel:
  // el panel vive en `body` por el portal, así que no está adentro de la fila.
  useEffect(() => {
    if (!abierto) return;
    const afuera = (ev: MouseEvent) => {
      const destino = ev.target as Node;
      if (filaRef.current?.contains(destino) || panelRef.current?.contains(destino)) return;
      setAbierto(null);
    };
    const tecla = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setAbierto(null);
    };
    document.addEventListener('mousedown', afuera);
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('mousedown', afuera);
      document.removeEventListener('keydown', tecla);
    };
  }, [abierto]);

  // La HOJA se cierra con Escape, como el panel. Tocar afuera lo resuelve su fondo
  // oscurecido, que es un elemento y no un `document` listener.
  useEffect(() => {
    if (!hojaAbierta) return;
    const tecla = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setHojaAbierta(false);
    };
    document.addEventListener('keydown', tecla);
    return () => document.removeEventListener('keydown', tecla);
  }, [hojaAbierta]);

  // Al pasar a escritorio —rotar la tablet, agrandar la ventana— la hoja deja de existir:
  // sin esto, el estado se quedaba en `true` y la hoja volvía sola al achicar de nuevo.
  useEffect(() => {
    if (!esMobile) setHojaAbierta(false);
  }, [esMobile]);

  const nombreDeFormato = (lista: typeof FORMATOS_IMAGEN, slug: string | null) =>
    slug ? proporcionDe(lista.find((f) => f.id === slug)?.etiqueta ?? slug) : AUTO;

  // Los campos de VIDEO. Ya no dependen de una mitad elegida: las dos familias se
  // configuran siempre, una debajo de la otra adentro del engranaje. Las calidades y los
  // formatos son dos preferencias distintas por tipo (no se pisan entre sí), y por eso
  // pueden estar las dos puestas a la vez sin ambigüedad.
  const camposDeVideo: CampoDePastilla[] = [
        {
          clave: 'calidadVideo',
          rotulo: 'Calidad',
          Icono: Gauge,
          ayuda: 'Estándar es más barata y llega a 15 segundos; Profesional usa el mejor modelo y llega a 30.',
          valor: CALIDADES_VIDEO.find((c) => c.id === preferencias.calidadVideo)?.etiqueta ?? AUTO,
          puesto: preferencias.calidadVideo !== null,
          anatomia: 'pastillas',
          conAutomatico: true,
          opciones: CALIDADES_VIDEO,
          elegido: preferencias.calidadVideo,
          elegir: (v) => onCambiar('calidadVideo', v as never),
        },
        {
          // La misma que ofrece la ruedita del Creador de video y la que `generate_video`
          // acepta en `resolution`
          // (ADR-2026-09-24-la-resolucion-de-video-tambien-es-una-preferencia). El costo
          // por segundo lo dice cada opción: es la razón para elegir una y no otra.
          clave: 'resolucionVideo',
          rotulo: 'Resolución',
          Icono: MonitorPlay,
          ayuda: `Cuántos píxeles tiene el video. Cambia el costo por segundo. En Profesional, 480p es un borrador: ${AYUDA_BORRADOR}`,
          valor:
            RESOLUCIONES_VIDEO.find((r) => r.id === preferencias.resolucionVideo)?.etiqueta ?? AUTO,
          puesto: preferencias.resolucionVideo !== null,
          anatomia: 'pastillas',
          conAutomatico: true,
          opciones: RESOLUCIONES_VIDEO,
          elegido: preferencias.resolucionVideo,
          elegir: (v) => onCambiar('resolucionVideo', v as never),
        },
  ];

  /** Los campos de IMAGEN. */
  const camposDeImagen: CampoDePastilla[] = [
        {
          clave: 'calidadImagen',
          rotulo: 'Calidad',
          Icono: Gauge,
          ayuda: 'La resolución de salida; cambia lo que cuesta cada imagen.',
          valor: CALIDADES_IMAGEN.find((c) => c.id === preferencias.calidadImagen)?.etiqueta ?? AUTO,
          puesto: preferencias.calidadImagen !== null,
          anatomia: 'pastillas',
          conAutomatico: true,
          opciones: CALIDADES_IMAGEN,
          elegido: preferencias.calidadImagen,
          elegir: (v) => onCambiar('calidadImagen', v as never),
        },
  ];


  /**
   * La INTELIGENCIA, cerrando la fila por la derecha: es lo último que se toca y lo que
   * vale para el turno entero, no para la generación.
   *
   * Los dos niveles pagos se DIBUJAN con candado en vez de esconderse —la convención de la
   * app, la misma que tenía el selector de la fila de abajo—: lo bloqueado se ve y dice qué
   * plan lo abre. "Baja" nunca se bloquea: es la que no consume créditos.
   */
  const campoInteligencia: CampoDePastilla = {
    clave: 'inteligencia',
    rotulo: 'Inteligencia',
    Icono: Brain,
    ayuda: 'Cuánto piensa el agente antes de contestar. Media y Alta consumen créditos por turno.',
    valor: INTELLIGENCE_LABEL[inteligencia],
    // Siempre en azul: no hay un "Auto" del que distinguirla — el nivel siempre está puesto.
    puesto: true,
    anatomia: 'filas',
    opciones: INTEL_OPTIONS.map((o) => ({
      id: o.id,
      etiqueta: o.label,
      detalle: o.desc,
      bloqueado: !puedeSubirInteligencia && o.id !== INTELIGENCIA_BASE,
    })),
    elegido: inteligencia,
    elegir: (v) => onCambiarInteligencia(v as Intelligence),
  };

  /**
   * EL FORMATO, UNO SOLO PARA LAS DOS FAMILIAS — pedido del usuario
   * (ADR-2026-09-24-el-formato-es-uno-solo-y-vive-afuera-de-las-familias).
   *
   * Era un campo por familia: dos listas con el mismo nombre, el mismo ícono y casi las
   * mismas opciones, una arriba de la otra. Nadie quiere sus fotos en 4:5 y sus videos en
   * 16:9 por accidente; quiere "lo mío es vertical".
   *
   * Se ofrecen los CINCO de video y no los ocho de imagen: son los que las dos familias
   * aceptan (`FORMATOS_VIDEO` ya es el subconjunto que `generate_video` admite). Elegir
   * escribe las DOS preferencias, que siguen siendo dos campos distintos en el modelo —el
   * agente las puede separar con su tool y el prompt las lee por separado—.
   *
   * Si las dos están en formatos distintos (las separó el agente, o quedó una imagen en
   * un formato que el video no acepta), el control muestra "Auto": no hay UN formato que
   * mostrar. Elegir cualquiera las vuelve a unir.
   */
  const formatoComun =
    preferencias.formatoImagen === preferencias.formatoVideo ? preferencias.formatoVideo : null;
  const campoFormato: CampoDePastilla = {
    clave: 'formato',
    rotulo: 'Formato',
    // La proporción del cuadro: el ícono ES la relación de aspecto.
    Icono: Ratio,
    ayuda: 'La proporción de lo que generes, en imagen y en video.',
    valor: nombreDeFormato(FORMATOS_VIDEO, formatoComun),
    puesto: formatoComun !== null,
    anatomia: 'pastillas',
    conAutomatico: true,
    opciones: FORMATOS_VIDEO,
    elegido: formatoComun,
    elegir: (v) => {
      onCambiar('formatoImagen', v as never);
      onCambiar('formatoVideo', v as never);
    },
  };

  /**
   * ── LOS DOS GRUPOS DEL ENGRANAJE ──────────────────────────────────────────
   *
   * Imagen y Video, cada uno con sus campos y su ícono, SIEMPRE los dos
   * (ADR-2026-09-24-la-fila-queda-en-inteligencia-y-un-engranaje). Antes había que elegir
   * una mitad en el switch para ver sus preferencias; ahora se dejan puestas las dos y el
   * agente usa las que correspondan a lo que termine generando.
   *
   * El ícono del grupo no es decoración: es lo que deja saltar a la familia que se busca
   * sin leer los rótulos, que es justo lo que pasa cuando las dos secciones tienen un
   * "Formato" y una "Calidad" que se llaman igual.
   */
  const gruposDeConfiguracion: {
    clave: string;
    titulo: string;
    Icono: typeof Brain;
    campos: CampoDePastilla[];
  }[] = [
    { clave: 'imagen', titulo: 'Imagen', Icono: ImageIcon, campos: camposDeImagen },
    { clave: 'video', titulo: 'Video', Icono: Film, campos: camposDeVideo },
  ];

  /** El único campo que sigue siendo una pastilla de la fila. */
  const campoAbierto = abierto === campoInteligencia.clave ? campoInteligencia : null;

  /** Las opciones que el panel dibuja, con "Automático" adelante si el campo lo pide. */
  const opcionesDelPanel = (campo: CampoDePastilla): OpcionDeCampo[] =>
    campo.conAutomatico
      ? [{ id: null, etiqueta: 'Automático' }, ...(campo.opciones ?? [])]
      : (campo.opciones ?? []);

  /** Elección única en CHIPS: valores cortos que se explican con su nombre. */
  const cuerpoDePastillas = (campo: CampoDePastilla) => {
    const todas = opcionesDelPanel(campo);
    const ids = todas.map((o) => o.id);
    // Los formatos llevan el cuadrito con su proporción; las calidades, no: "2K" no
    // tiene una forma que dibujar.
    const conProporcion = campo.clave === 'formatoImagen' || campo.clave === 'formatoVideo';
    return (
      <div className="flex flex-wrap gap-2">
        {todas.map((o) => {
          const activo = o.id === campo.elegido;
          const elegir = (v: string | number | null) => campo.elegir?.(v);
          return (
            <button
              key={o.id ?? 'auto'}
              type="button"
              role="radio"
              aria-checked={activo}
              {...propsDeRadio(ids, campo.elegido ?? null, o.id, elegir)}
              onClick={() => elegir(o.id)}
              // Elegido = azul clarito con borde azul
              // (ADR-2026-09-22-lo-elegido-del-menu-vuelve-a-ser-azul). El azul se fue un
              // rato de todo el control y volvió sólo ACÁ ADENTRO: en la fila, la pastilla
              // muestra un dato y va en negro; en el menú, esto es lo que está elegido, y
              // "elegido" en esta app es azul clarito con borde azul.
              className={`inline-flex items-center gap-1.5 rounded-full py-1.5 px-3 text-[12.5px] font-semibold transition-colors ${
                activo
                  ? 'bg-[#E9EDFD] text-brand-primary ring-1 ring-brand-primary'
                  : 'bg-neutral-100 text-[#02062BE6] hover:bg-neutral-200'
              }`}
            >
              {conProporcion && <GlifoDeProporcion etiqueta={o.etiqueta} activo={activo} />}
              {o.etiqueta}
              {o.detalle && (
                <span
                  className={`ml-1 font-normal ${
                    activo ? 'text-brand-primary' : 'text-[#02062B]/[0.45]'
                  }`}
                >
                  · {o.detalle}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  };

  /** Elección única en FILAS: cada opción con su bajada. La misma anatomía que tenían el
   *  Motor y la Inteligencia adentro del menú del "+", para que cambie el lugar y no lo
   *  que se lee. */
  const cuerpoDeFilas = (campo: CampoDePastilla) => {
    const todas = opcionesDelPanel(campo);
    // Las bloqueadas salen de la navegación por flechas: no se pueden elegir, así que
    // pararse en ellas sería un callejón. Siguen anunciadas con su motivo.
    const ids = todas.filter((o) => !o.bloqueado).map((o) => o.id);
    return (
      <div className="-mx-1">
        {todas.map((o) => {
          const activo = o.id === campo.elegido;
          const elegir = (v: string | number | null) => campo.elegir?.(v);
          return (
            <button
              key={o.id ?? 'auto'}
              type="button"
              role="radio"
              aria-checked={activo}
              // No `disabled`: un `disabled` se saltea en la navegación por teclado y el
              // lector de pantalla no cuenta la opción. Queda anunciada, con el motivo.
              aria-disabled={o.bloqueado}
              aria-label={
                o.bloqueado ? `${o.etiqueta} — disponible con los planes Pro y Ultra` : undefined
              }
              {...(o.bloqueado ? {} : propsDeRadio(ids, campo.elegido ?? null, o.id, elegir))}
              onClick={() => {
                if (o.bloqueado) return;
                elegir(o.id);
              }}
              className={`flex w-full items-start gap-3 rounded-xl py-2 px-3 text-left transition-colors ${
                o.bloqueado
                  ? 'cursor-not-allowed opacity-55'
                  : activo
                    ? 'bg-[#E9EDFD]'
                    : 'hover:bg-[#02062B]/[0.04]'
              }`}
            >
              <span className="min-w-0 flex-1">
                <span
                  className={`block truncate text-[13.5px] ${
                    activo && !o.bloqueado
                      ? 'font-semibold text-brand-primary'
                      : 'font-medium text-brand-black'
                  }`}
                >
                  {o.etiqueta}
                </span>
                {o.detalle && (
                  <span className="mt-0.5 block text-[11.5px] leading-snug text-[#02062B]/[0.5]">
                    {o.detalle}
                  </span>
                )}
              </span>
              {o.bloqueado ? (
                <Lock className="mt-0.5 h-4 w-4 shrink-0 text-[#02062B]/[0.35]" aria-hidden="true" />
              ) : (
                /* Como en los toggles: la marca no es sólo color. */
                <span
                  aria-hidden="true"
                  className={`mt-0.5 inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border transition-colors ${
                    activo
                      ? 'border-brand-primary bg-brand-primary text-white'
                      : 'border-[#02062B]/[0.18] bg-white'
                  }`}
                >
                  {activo && <Check className="h-3 w-3" strokeWidth={3} />}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  };

  /** El cuerpo que le toca a cada anatomía. */
  const cuerpoDelCampo = (campo: CampoDePastilla) =>
    campo.anatomia === 'pastillas' ? cuerpoDePastillas(campo) : cuerpoDeFilas(campo);

  /**
   * ── EL CONTENIDO DEL ENGRANAJE ────────────────────────────────────────────
   *
   * Las dos familias, una debajo de la otra, cada una encabezada por su ícono y su
   * nombre, y adentro un bloque por campo con su ícono y su rótulo. El MISMO cuerpo de
   * controles que dibujaba cada pastilla por separado: cambia el envase, no lo que se
   * toca.
   *
   * SIN la línea de ayuda debajo de cada rótulo (pedido del usuario): con cuatro campos
   * cuyos nombres —Formato, Calidad, Resolución— dicen lo que son, y con las opciones a
   * la vista, la ayuda era un renglón gris por campo que alargaba el menú sin agregar
   * nada. Lo que SÍ quedó es el detalle de cada opción ("· 5 cr/s", "· hasta 15 s"): eso
   * no explica el campo, es la razón para elegir una opción y no la otra. La ayuda sigue
   * viajando en el `aria-label` de cada pastilla de la fila.
   *
   * Es uno solo para los dos envases —el panel colgado del engranaje en la computadora y
   * la hoja que sube desde abajo en el teléfono—: dos copias de esto se desincronizan al
   * primer campo nuevo.
   */
  /** Un campo: su ícono, su rótulo y sus controles. */
  const bloqueDeCampo = (campo: CampoDePastilla, etiqueta: string) => (
    <div key={campo.clave} role="radiogroup" aria-label={etiqueta}>
      <p className="mb-2.5 flex items-center gap-1.5 text-[12.5px] font-semibold text-brand-black">
        <campo.Icono
          className="h-3.5 w-3.5 shrink-0 text-[#02062B]/[0.55]"
          strokeWidth={2.25}
          aria-hidden="true"
        />
        {campo.rotulo}
      </p>
      {cuerpoDelCampo(campo)}
    </div>
  );

  const contenidoDeConfiguracion = (
    <div className="flex flex-col">
      {/* El Formato, AFUERA de las dos familias: vale para las dos. */}
      <div className="pt-1 pb-5">{bloqueDeCampo(campoFormato, 'Formato')}</div>

      {gruposDeConfiguracion.map((grupo) => (
        <section
          key={grupo.clave}
          aria-label={grupo.titulo}
          className="border-t border-black/[0.06] py-5"
        >
          {/* El encabezado de la familia: 16px contra los 12.5 de los campos que tiene
              adentro. Es lo ÚNICO que separa dos secciones que siguen compartiendo una
              "Calidad" que se llama igual, así que tiene que ganarles de lejos — a 13.5
              se leía como un campo más en negrita. El ícono crece con él. */}
          <p className="flex items-center gap-2 text-[16px] font-semibold text-brand-black">
            <grupo.Icono
              className="h-[18px] w-[18px] shrink-0 text-brand-primary"
              strokeWidth={2.25}
              aria-hidden="true"
            />
            {grupo.titulo}
          </p>

          {grupo.campos.map((campo) => (
            <div key={campo.clave} className="mt-5">
              {bloqueDeCampo(campo, `${grupo.titulo}: ${campo.rotulo}`)}
            </div>
          ))}

        </section>
      ))}
    </div>
  );

  // EL PANEL, por portal: colgado del borde inferior izquierdo del control que se tocó.
  // Sirve a los dos —la pastilla de Inteligencia y el engranaje—, que es lo que mantiene
  // una sola forma de "menú de la fila".
  const panel =
    abierto && pos && (campoAbierto || abierto === CLAVE_CONFIG)
      ? createPortal(
          <div
            ref={panelRef}
            role={campoAbierto ? 'radiogroup' : 'dialog'}
            aria-label={campoAbierto ? campoAbierto.rotulo : 'Opciones de generación'}
            style={{
              position: 'fixed',
              left: pos.left,
              width: pos.width,
              ...(pos.top !== undefined ? { top: pos.top } : { bottom: pos.bottom }),
              maxHeight: pos.alto,
            }}
            className="z-[120] overflow-y-auto rounded-2xl border border-neutral-200 bg-white p-3 shadow-[0_8px_30px_rgba(16,24,45,0.14)] animate-fade-in"
          >
            {campoAbierto ? (
              <>
                <p className="text-[12.5px] font-semibold text-brand-black">{campoAbierto.rotulo}</p>
                <p className="mb-2 text-[11.5px] leading-snug text-[#02062B]/[0.5]">{campoAbierto.ayuda}</p>
                {cuerpoDelCampo(campoAbierto)}
              </>
            ) : (
              contenidoDeConfiguracion
            )}
          </div>,
          document.body,
        )
      : null;

  /**
   * La PASTILLA: ícono + valor + chevron. Una sola función para las tres que existen —las
   * de cada campo, la del tipo de salida en el teléfono y la de "Configuración"—, porque
   * lo que las hace reconocibles como grupo (alto, radio, colores, el estado abierto) no
   * puede vivir en tres lugares.
   *
   * `texto` es el valor; cuando el campo no tiene (el tipo de salida, Configuración) se
   * dibuja su rótulo, que ahí ES lo que la pastilla dice.
   */
  const pastilla = (
    campo: Pick<CampoDePastilla, 'clave' | 'rotulo' | 'Icono' | 'valor'>,
    estaAbierta: boolean,
    alTocar: () => void,
  ) => (
    <button
      key={campo.clave}
      ref={(el) => {
        // El nodo vivo de cada pastilla: es lo que mide `medir` para plantar el panel
        // debajo de ELLA. Se saca del mapa al desmontarse (la fila cambia de campos al
        // pasar de Imagen a Video, y entera al cruzar el breakpoint del teléfono).
        if (el) pastillasRef.current.set(campo.clave, el);
        else pastillasRef.current.delete(campo.clave);
      }}
      type="button"
      aria-haspopup="true"
      aria-expanded={estaAbierta}
      // En el teléfono el rótulo se esconde, así que el nombre del campo lo lleva
      // también el `aria-label`.
      aria-label={campo.valor ? `${campo.rotulo}: ${campo.valor}` : campo.rotulo}
      onClick={alTocar}
      // La MISMA pastilla que el selector de Inteligencia de adentro del agente
      // (`SelectorInteligencia`): mismo alto, colores y rótulo, así la perilla se reconoce
      // igual en la home y en el chat (ADR-2026-09-25-la-inteligencia-de-la-home-es-la-del-agente).
      className={`inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-[13px] font-medium transition-colors ${
        estaAbierta
          ? 'bg-[#02062B]/[0.09] text-brand-black'
          : 'bg-[#F0F2F7] text-[#02062B]/[0.7] hover:bg-[#02062B]/[0.09]'
      }`}
    >
      {/* El ícono de la pastilla, con el mismo tamaño y grosor que el del switch de
          Imagen / Video: es lo que se reconoce antes de leer, y lo que hace que la fila
          se recorra de un vistazo. Va en el gris del rótulo y no en azul: el color de
          esta pastilla lo lleva el VALOR, que es lo que cambia. */}
      <campo.Icono className="h-4 w-4 shrink-0" strokeWidth={2.25} aria-hidden="true" />
      {/* Con rótulo, como en el agente: "Inteligencia Baja", el nivel en azul. Supersede
          para esta pastilla ADR-2026-09-22-el-rotulo-vive-solo-en-el-menu, que era de la
          fila de seis pastillas; hoy la fila es la Inteligencia y el engranaje, y la
          palabra entra. En el teléfono se esconde, igual que en el agente. */}
      <span className={compactas ? 'hidden' : 'hidden sm:inline'}>{campo.rotulo}</span>
      <span className="text-brand-primary">{campo.valor || campo.rotulo}</span>
      <ChevronDown
        className={`h-3.5 w-3.5 shrink-0 opacity-60 transition-transform ${
          estaAbierta ? 'rotate-180' : ''
        }`}
        aria-hidden="true"
      />
    </button>
  );

  /**
   * ── LA HOJA DE CONFIGURACIÓN (sólo teléfono) ──────────────────────────────
   *
   * TODAS las opciones, una sección por campo, en una hoja que sube desde abajo: la misma
   * anatomía que el menú del "+" y que las hojas de Historial y Memoria (fondo oscurecido,
   * radio 20 arriba, manija, `85dvh` de tope y el `safe-area` abajo).
   *
   * Cada sección es el rótulo, la ayuda y el MISMO cuerpo que dibuja el panel de esa
   * pastilla en escritorio: no hay una segunda versión de los controles, sólo otro envase.
   */
  const hoja =
    esMobile && hojaAbierta
      ? createPortal(
          <>
            <div
              className="fixed inset-0 z-[130] bg-black/40 animate-fade-in"
              onClick={() => setHojaAbierta(false)}
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Configuración"
              className="fixed inset-x-0 bottom-0 z-[131] flex flex-col rounded-t-[20px] bg-white shadow-2xl animate-slide-up-sheet"
              style={{
                maxHeight: '85dvh',
                paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
              }}
            >
              <div className="shrink-0 py-2">
                <div className="mx-auto h-[5px] w-9 rounded-full bg-black/[0.15]" />
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-2">
                {/* El MISMO contenido que el panel de escritorio: el formato y las dos
                    familias. La hoja es sólo otro envase, y SIN título propio: los
                    encabezados de adentro (Imagen, Video) ya dicen qué es esto. */}
                {contenidoDeConfiguracion}
              </div>
            </div>
          </>,
          document.body,
        )
      : null;

  /**
   * EL ENGRANAJE. No es una pastilla: es un botón redondo de sólo ícono, del alto de la
   * fila (ADR-2026-09-24-la-fila-queda-en-inteligencia-y-un-engranaje). Que no tenga la
   * forma de las pastillas es a propósito — las pastillas muestran un valor, y ésta no
   * muestra ninguno: abre el resto.
   *
   * En la computadora abre el panel colgado; en el teléfono, la hoja. Es el mismo
   * contenido y el mismo botón.
   */
  const engranaje = (
    <button
      ref={(el) => {
        if (el) pastillasRef.current.set(CLAVE_CONFIG, el);
        else pastillasRef.current.delete(CLAVE_CONFIG);
      }}
      type="button"
      aria-haspopup="dialog"
      aria-expanded={esMobile ? hojaAbierta : abierto === CLAVE_CONFIG}
      aria-label="Opciones de generación"
      title="Opciones de generación"
      onClick={() => {
        if (esMobile) {
          // El panel flotante y la hoja no conviven: abrir una cierra el otro.
          setAbierto(null);
          setHojaAbierta((v) => !v);
          return;
        }
        setAbierto((v) => (v === CLAVE_CONFIG ? null : CLAVE_CONFIG));
      }}
      className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors ${
        (esMobile ? hojaAbierta : abierto === CLAVE_CONFIG)
          ? 'bg-[#02062B]/[0.09] text-brand-black'
          : 'bg-[#F0F2F7] text-[#02062B]/[0.7] hover:bg-[#02062B]/[0.09]'
      }`}
    >
      <Settings className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
    </button>
  );

  return (
    <div
      ref={filaRef}
      // ── LA FILA, IGUAL EN LOS DOS TAMAÑOS ─────────────────────────────────
      // Dos controles: la Inteligencia y el engranaje
      // (ADR-2026-09-24-la-fila-queda-en-inteligencia-y-un-engranaje). Ya no hace falta
      // una fila distinta para el teléfono —lo que no entraba a 390px era el switch más
      // seis pastillas (ADR-2026-09-22-la-fila-de-pastillas-en-el-telefono)—: dos
      // controles entran en cualquier ancho. Lo único que sigue cambiando por tamaño es
      // el ENVASE del engranaje: panel colgado en la computadora, hoja en el teléfono.
      className={`relative flex flex-nowrap items-center gap-1.5 overflow-x-auto [scrollbar-width:none] sm:flex-wrap sm:overflow-visible [&::-webkit-scrollbar]:hidden ${className}`}
    >
      {panel}
      {hoja}

      {/* La Inteligencia PRIMERO y el engranaje a su derecha
          (ADR-2026-09-25-la-inteligencia-de-la-home-es-la-del-agente, supersede el orden de
          ADR-2026-09-24-la-fila-queda-en-inteligencia-y-un-engranaje): es lo que se cambia
          más seguido y el mismo lugar que tiene dentro del agente, al lado del "+". */}
      {pastilla(campoInteligencia, abierto === campoInteligencia.clave, () =>
        setAbierto((v) => (v === campoInteligencia.clave ? null : campoInteligencia.clave)),
      )}
      {engranaje}
    </div>
  );
};

export default PastillasDeContenido;
